// Dashboard Messages widget — recent unread customer threads + inline chat popup.
//
// Self-contained: fetches GET /api/portal/messages (the same endpoint the
// Messages page uses) and consumes `supplierThreads` (threadsForSupplier output
// — customer↔retailer threads, retailer in the supplier slot). Lists the most
// recent unread threads; clicking one opens an inline chat popup with the full
// history + a reply box that sends via the existing POST /api/portal/customer-message.
// Live via the `message.received` WS event. Small bubble/helper bits are
// duplicated here rather than extracted from the Messages page.

import type { PageContext } from "../../shell/types";
import * as realtime from "../../shell/realtime";
import { attachAutoGrow, resetAutoGrow } from "../../shell/auto-grow";
import "./messages-widget.css";

interface ThreadMsg { id: string; from: "me" | "them"; text: string; ts: number; readAt?: number | null; }
interface SupplierThread {
  id: string;
  retailer: string;        // customer display name
  retailerId: string;      // customer key (email/slug)
  retailerEmail: string;   // customer email
  subject: string;
  messages: ThreadMsg[];
  unread: number;
  lastTs: number;
  lastMessage: string;
}
interface MessagesResp { ok?: boolean; supplierThreads?: SupplierThread[]; }

const ROW_LIMIT = 5;
const PALETTE = ["#e58200", "#3b82f6", "#a855f7", "#22c55e", "#0ea5e9", "#ef4444", "#f59e0b"];

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}
function initials(name: string): string {
  return (name.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase()) || "?";
}
function colorFor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
}
function relTime(ms: number): string {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 30 ? `${d}d ago` : `${Math.round(d / 30)}mo ago`;
}
const SEEN_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});
// "Seen" indicator markup for an outbound message the customer has read.
function seenHtml(m: ThreadMsg): string {
  if (m.from !== "me" || typeof m.readAt !== "number" || m.readAt <= 0) return "";
  return `<span class="dmw-bubble__seen" title="Customer read this message">Seen ${esc(SEEN_FMT.format(new Date(m.readAt)).replace(",", ""))}</span>`;
}

export function mountMessagesWidget(host: HTMLElement, ctx: PageContext, retailerId: string): () => void {
  let destroyed = false;
  let threads: SupplierThread[] = [];
  let openId: string | null = null;
  let overlay: HTMLElement | null = null;

  const load = async (): Promise<void> => {
    try {
      const enc = encodeURIComponent(retailerId);
      const resp = await ctx.api<MessagesResp>(`/portal/messages?retailerId=${enc}&supplierId=${enc}`);
      threads = (resp.supplierThreads ?? [])
        .filter((t) => (t.unread || 0) > 0)
        .sort((a, b) => b.lastTs - a.lastTs);
    } catch {
      // leave previous threads in place on a transient failure
    }
    if (destroyed) return;
    renderRows();
    if (openId) {
      const t = threads.find((x) => x.id === openId) ?? allThreads().find((x) => x.id === openId);
      if (t && overlay) renderPopupBody(t);
    }
  };

  // Keep a reference to the last full fetch (incl. read threads) so an open
  // popup still resolves after its unread count drops to 0.
  let lastFetched: SupplierThread[] = [];
  const allThreads = (): SupplierThread[] => lastFetched;

  const renderRows = (): void => {
    lastFetched = threads.slice();
    if (threads.length === 0) {
      host.innerHTML = `<li class="dash-empty">No unread messages.</li>`;
      return;
    }
    host.innerHTML = threads.slice(0, ROW_LIMIT).map((t) => `
      <li class="dash-msg" data-thread="${esc(t.id)}" style="cursor:pointer">
        <div class="dash-msg__row1">
          <span class="dash-msg__unread-dot" aria-label="${t.unread || 0} unread"
                style="width:8px;height:8px;border-radius:50%;background:var(--color-amber,#F59E0B);flex:0 0 auto;align-self:center"></span>
          <span class="dmw-avatar" style="background:${colorFor(t.retailer || t.retailerEmail || "?")}">${esc(initials(t.retailer || t.retailerEmail || "?"))}</span>
          <span class="dash-msg__name">${esc(t.retailer || t.retailerEmail || "Customer")}</span>
          <span class="dash-msg__when">${esc(relTime(t.lastTs))}</span>
        </div>
        <div class="dash-msg__preview">${esc(t.lastMessage || "")}</div>
      </li>`).join("");
    host.querySelectorAll<HTMLElement>("[data-thread]").forEach((li) => {
      li.addEventListener("click", () => openPopup(li.dataset["thread"] || ""));
    });
  };

  // ── Inline chat popup ──────────────────────────────────────────────────
  const closePopup = (): void => {
    openId = null;
    if (overlay) { overlay.remove(); overlay = null; }
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") closePopup(); };

  const openPopup = (id: string): void => {
    const t = threads.find((x) => x.id === id);
    if (!t) return;
    openId = id;
    overlay = document.createElement("div");
    overlay.className = "dmw-overlay";
    overlay.innerHTML = `
      <div class="dmw-popup" role="dialog" aria-modal="true" aria-label="Chat with ${esc(t.retailer)}">
        <div class="dmw-popup__head">
          <span class="dmw-avatar" style="background:${colorFor(t.retailer)}">${esc(initials(t.retailer))}</span>
          <span class="dmw-popup__name">${esc(t.retailer || "Customer")}</span>
          <button type="button" class="dmw-popup__x" data-role="close" aria-label="Close">&times;</button>
        </div>
        <div class="dmw-popup__msgs" data-role="msgs"></div>
        <form class="dmw-popup__foot" data-role="form">
          <textarea class="dmw-popup__input" data-role="input" rows="1" placeholder="Reply to ${esc((t.retailer || "").split(" ")[0] || "customer")}…"></textarea>
          <button type="submit" class="dmw-popup__send" aria-label="Send">Send</button>
        </form>
      </div>`;
    overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) closePopup(); });
    overlay.querySelector<HTMLButtonElement>('[data-role="close"]')!.addEventListener("click", closePopup);

    const form = overlay.querySelector<HTMLFormElement>('[data-role="form"]')!;
    form.addEventListener("submit", (e) => { e.preventDefault(); void sendReply(t); });

    document.addEventListener("keydown", onKey);
    document.body.appendChild(overlay);
    renderPopupBody(t);
    const inputEl = overlay.querySelector<HTMLTextAreaElement>('[data-role="input"]');
    if (inputEl) { attachAutoGrow(inputEl); inputEl.focus(); }
  };

  const renderPopupBody = (t: SupplierThread): void => {
    if (!overlay) return;
    const msgsEl = overlay.querySelector<HTMLElement>('[data-role="msgs"]');
    if (!msgsEl) return;
    // Preserve any in-progress reply draft across a live refresh.
    const draftEl = overlay.querySelector<HTMLTextAreaElement>('[data-role="input"]');
    const draft = draftEl ? draftEl.value : "";
    msgsEl.innerHTML = (t.messages || []).map((m) => `
      <div class="dmw-bubble dmw-bubble--${m.from === "me" ? "me" : "them"}">
        <div class="dmw-bubble__text">${esc(m.text || "")}</div>
        <div class="dmw-bubble__time">${esc(relTime(m.ts))}${seenHtml(m) ? ` · ${seenHtml(m)}` : ""}</div>
      </div>`).join("");
    msgsEl.scrollTop = msgsEl.scrollHeight;
    if (draftEl) draftEl.value = draft;
  };

  const sendReply = async (t: SupplierThread): Promise<void> => {
    if (!overlay) return;
    const input = overlay.querySelector<HTMLTextAreaElement>('[data-role="input"]')!;
    const body = input.value.trim();
    if (!body) return;
    input.value = "";
    resetAutoGrow(input);
    // Optimistic bubble.
    t.messages = [...(t.messages || []), { id: "tmp-" + Date.now(), from: "me", text: body, ts: Date.now() }];
    renderPopupBody(t);
    try {
      const resp = await ctx.api<{ ok: boolean; error?: string }>("/portal/customer-message", {
        method: "POST",
        body: JSON.stringify({
          retailerId,
          retailerName: ctx.user?.retailerName,
          customerEmail: t.retailerEmail,
          customerName: t.retailer,
          subject: t.subject || "Re: your message",
          body,
        }),
      });
      if (!resp.ok) throw new Error(resp.error || "send failed");
      ctx.track({ eventType: "action", feature: "messages", customData: { action: "message-sent", from: "dashboard-widget" } });
      void load();
    } catch {
      ctx.showToast("Couldn't send message. Please try again.", "error");
    }
  };

  const off = realtime.onEvent("message.received", () => { if (!destroyed) void load(); });
  void load();

  return () => {
    destroyed = true;
    off();
    closePopup();
    host.innerHTML = "";
  };
}

// ── Standalone chat popup for an arbitrary contact (used by the dashboard FABs)
// Reuses the .dmw-* popup styling. Loads the contact's existing thread history
// (if any) and lets the retailer send via /api/portal/customer-message.
export async function openChatForContact(
  ctx: PageContext,
  retailerId: string,
  contact: { name: string; email: string },
): Promise<void> {
  if (!contact.email) { ctx.showToast("No email on file for this contact", "info"); return; }
  let thread: SupplierThread | null = null;
  try {
    const enc = encodeURIComponent(retailerId);
    const resp = await ctx.api<MessagesResp>(`/portal/messages?retailerId=${enc}&supplierId=${enc}`);
    thread = (resp.supplierThreads ?? []).find(
      (t) => (t.retailerEmail || "").toLowerCase() === contact.email.toLowerCase(),
    ) ?? null;
  } catch { thread = null; }
  if (!thread) {
    thread = { id: "new:" + contact.email, retailer: contact.name, retailerId: contact.email, retailerEmail: contact.email, subject: "", messages: [], unread: 0, lastTs: 0, lastMessage: "" };
  }

  const overlay = document.createElement("div");
  overlay.className = "dmw-overlay";
  const t = thread;
  overlay.innerHTML = `
    <div class="dmw-popup" role="dialog" aria-modal="true" aria-label="Chat with ${esc(t.retailer)}">
      <div class="dmw-popup__head">
        <span class="dmw-avatar" style="background:${colorFor(t.retailer || t.retailerEmail)}">${esc(initials(t.retailer || t.retailerEmail))}</span>
        <span class="dmw-popup__name">${esc(t.retailer || "Customer")}</span>
        <button type="button" class="dmw-popup__x" data-role="close" aria-label="Close">&times;</button>
      </div>
      <div class="dmw-popup__msgs" data-role="msgs"></div>
      <form class="dmw-popup__foot" data-role="form">
        <textarea class="dmw-popup__input" data-role="input" rows="1" placeholder="Message ${esc((t.retailer || "").split(" ")[0] || "customer")}…"></textarea>
        <button type="submit" class="dmw-popup__send" aria-label="Send">Send</button>
      </form>
    </div>`;
  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") close(); };
  function close(): void { overlay.remove(); document.removeEventListener("keydown", onKey); }
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector<HTMLButtonElement>('[data-role="close"]')!.addEventListener("click", close);

  const msgsEl = overlay.querySelector<HTMLElement>('[data-role="msgs"]')!;
  const paintBody = (): void => {
    const draftEl = overlay.querySelector<HTMLTextAreaElement>('[data-role="input"]');
    const draft = draftEl ? draftEl.value : "";
    msgsEl.innerHTML = (t.messages || []).map((m) => `
      <div class="dmw-bubble dmw-bubble--${m.from === "me" ? "me" : "them"}">
        <div class="dmw-bubble__text">${esc(m.text || "")}</div>
        <div class="dmw-bubble__time">${esc(relTime(m.ts))}${seenHtml(m) ? ` · ${seenHtml(m)}` : ""}</div>
      </div>`).join("") || `<div class="dmw-bubble dmw-bubble--them"><div class="dmw-bubble__text">No messages yet — say hello.</div></div>`;
    msgsEl.scrollTop = msgsEl.scrollHeight;
    if (draftEl) draftEl.value = draft;
  };
  overlay.querySelector<HTMLFormElement>('[data-role="form"]')!.addEventListener("submit", (e) => {
    e.preventDefault();
    const input = overlay.querySelector<HTMLTextAreaElement>('[data-role="input"]')!;
    const body = input.value.trim();
    if (!body) return;
    input.value = "";
    resetAutoGrow(input);
    t.messages = [...(t.messages || []), { id: "tmp-" + Date.now(), from: "me", text: body, ts: Date.now() }];
    paintBody();
    void (async () => {
      try {
        const resp = await ctx.api<{ ok: boolean; error?: string }>("/portal/customer-message", {
          method: "POST",
          body: JSON.stringify({
            retailerId, retailerName: ctx.user?.retailerName,
            customerEmail: t.retailerEmail, customerName: t.retailer,
            subject: t.subject || "Message from your retailer", body,
          }),
        });
        if (!resp.ok) throw new Error(resp.error || "send failed");
        ctx.track({ eventType: "action", feature: "messages", customData: { action: "message-sent", from: "dashboard-fab" } });
      } catch { ctx.showToast("Couldn't send message. Please try again.", "error"); }
    })();
  });

  document.addEventListener("keydown", onKey);
  document.body.appendChild(overlay);
  paintBody();
  const inputEl2 = overlay.querySelector<HTMLTextAreaElement>('[data-role="input"]');
  if (inputEl2) { attachAutoGrow(inputEl2); inputEl2.focus(); }
}
